import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { formatCurrencyNoDecimals, formatDate } from '@if/core/utils'
import { Link } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LoanDashboardEntry } from './loan-filters-schema'

export const useLoanListColumns = (): GridColDef<LoanDashboardEntry>[] => {
  const { t } = useTranslation()
  return useMemo<GridColDef<LoanDashboardEntry>[]>(
    () => [
      {
        field: 'referenceNumber',
        headerName: t('loan.loanList.referenceNumber'),
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          return (
            <Link href={`/loans/${params.row.financingProgramId}/${params.row.loanId}/calendar`}>
              {params.row.contractNumber}
            </Link>
          )
        },
      },
      {
        field: 'financingProgramId',
        headerName: t('loan.loanList.financingProgram'),
        minWidth: 210,
        sortable: false,
        valueFormatter: (financingProgram: EFinancingProgram) => {
          return t(`EFinancingProgram.${financingProgram}`, { ns: 'enum' })
        },
      },
      {
        field: 'financingCompanyId',
        headerName: t('loan.loanList.financingCompany'),
        flex: 1,
        minWidth: 210,
        sortable: false,
        valueFormatter: (financingCompany: EFinancingCompany) => {
          switch (financingCompany) {
            case EFinancingCompany.IFinance:
            case EFinancingCompany.Iceberg:
              return financingCompany
            default:
              return ''
          }
        },
      },
      {
        field: 'borrowerFullName',
        headerName: t('loan.loanList.borrower'),
        flex: 1,
        sortable: false,
      },
      {
        field: 'status',
        headerName: t('loan.loanList.status'),
        flex: 1,
        sortable: false,
      },
      {
        field: 'createdOn',
        headerName: t('loan.loanList.createdOn'),
        sortable: false,
        minWidth: 130,
        valueFormatter: (createdOn: Date) => {
          if (createdOn.toString() === '0001-01-01T00:00:00') return ''
          return formatDate(createdOn)
        },
      },
      {
        field: 'loanAmount',
        headerName: t('loan.loanList.loanAmount'),
        minWidth: 150,
        sortable: false,
        valueFormatter: (loanAmount) => {
          return formatCurrencyNoDecimals(loanAmount ?? 0)
        },
      },
      {
        field: 'loanBalance',
        headerName: t('loan.loanList.loanBalance'),
        minWidth: 200,
        sortable: false,
        valueFormatter: (loanBalance) => {
          return formatCurrencyNoDecimals(loanBalance ?? 0)
        },
      },
    ],
    [t],
  )
}
